import { useEffect, useState } from 'react';
import { uploadEventPhoto } from '../../Services/arndAPI';
import { isPhotoFile, remoteName } from './photoUploaderFiles';

/**
 * SCRUM-427: the upload run lives OUTSIDE React. The queue used to be refs
 * inside PhotoUploader, so collapsing the "Upload photos" panel or leaving
 * the ops page unmounted it mid-run: the files in flight finished unseen,
 * nothing after them started, and the panel came back empty. One queue per
 * event, held here for the life of the tab; the component only subscribes
 * and renders. A full reload is the one thing that still kills a run — the
 * queue asks the browser to confirm it while files are moving.
 *
 * Behaviour is the SCRUM-415 pusher's: PARALLEL at a time, RETRIES attempts
 * on a server-side failure, unlimited wait-and-retry (backoff capped at
 * MAX_BACKOFF_MS) when the connection drops. Every finished file is
 * appended to the per-event ledger in localStorage.
 */
export const PARALLEL = 3;
export const RETRIES = 3;
export const MAX_BACKOFF_MS = 30_000;
const ledgerKey = (eventId) => `ops.upload.${eventId}`;

export { isPhotoFile, remoteName } from './photoUploaderFiles';

export const readLedger = (eventId) => {
  try {
    const raw = localStorage.getItem(ledgerKey(eventId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};
const appendLedger = (eventId, entry) => {
  try {
    const rows = readLedger(eventId);
    rows.push(entry);
    localStorage.setItem(ledgerKey(eventId), JSON.stringify(rows));
  } catch { /* storage full or disabled: the on-screen list still has it */ }
};

const ACTIVE = ['queued', 'uploading', 'retrying'];

export class UploadQueue {
  constructor(eventId, prefix = '') {
    this.eventId = eventId;
    this.prefix = prefix;
    this.items = [];
    this.inFlight = 0;
    this.running = false;
    this.controllers = new Map();
    this.listeners = new Set();
    /** Names already in the event (set by the panel when it loads the server list). */
    this.serverNames = null;
    /** SCRUM-418: files left out as not-photos (macOS `._` sidecars,
     * dotfiles, non-image extensions) — shown so the operator knows the
     * count is lower on purpose, not because something failed. */
    this.ignored = 0;
    this.beforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
  }

  /** fn(kind) — 'progress' for a byte tick, 'change' for anything else, 'idle' when a run ends. */
  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  emit(kind) {
    for (const fn of this.listeners) fn(kind);
  }

  get pending() { return this.items.filter((i) => ACTIVE.includes(i.status)); }
  count(status) { return this.items.filter((i) => i.status === status).length; }

  addFiles(fileList) {
    const known = new Set(this.items.map((i) => i.name));
    let added = 0;
    for (const file of Array.from(fileList)) {
      if (!isPhotoFile(file.name)) { this.ignored += 1; continue; }
      const name = remoteName(this.prefix, file.name);
      if (known.has(name)) continue;
      known.add(name);
      const dup = this.serverNames?.has(name);
      this.items.push({
        id: `${name}-${file.size}-${file.lastModified}`,
        file, name, size: file.size,
        status: dup ? 'duplicate' : 'queued',
        error: dup ? 'already on server' : null,
        progress: 0, attempts: 0, at: dup ? new Date() : null,
      });
      added++;
    }
    this.emit('change');
    return added;
  }

  finish(item, status, error) {
    item.status = status;
    item.error = error || null;
    item.at = new Date();
    item.progress = status === 'pushed' ? 1 : item.progress;
    appendLedger(this.eventId, { name: item.name, size: item.size, status, error: item.error, at: item.at.toISOString() });
    if (status === 'pushed') this.serverNames?.add(item.name);
  }

  setRunning(on) {
    if (on === this.running) return;
    this.running = on;
    if (typeof window !== 'undefined') {
      if (on) window.addEventListener('beforeunload', this.beforeUnload);
      else window.removeEventListener('beforeunload', this.beforeUnload);
    }
  }

  pump() {
    if (!this.running) return;
    while (this.inFlight < PARALLEL) {
      const item = this.items.find((i) => i.status === 'queued');
      if (!item) break;
      item.status = 'uploading';
      item.progress = 0;
      item.attempts += 1;
      this.inFlight += 1;
      const ctrl = new AbortController();
      this.controllers.set(item.id, ctrl);
      this.emit('change');
      uploadEventPhoto(this.eventId, item.file, this.prefix, {
        signal: ctrl.signal,
        onProgress: (p) => { item.progress = p; this.emit('progress'); },
      })
        .then((r) => {
          if (r.status === 'pushed') this.finish(item, 'pushed');
          else if (r.status === 'duplicate') this.finish(item, 'duplicate', 'already on server');
          // SCRUM-418/435: the API refused it as not-a-photo — terminal, no retry
          else if (r.status === 'skipped') this.finish(item, 'skipped', r.error || 'not a photo');
          else throw new Error(r.error || 'FTP push failed');
        })
        .catch((e) => {
          if (ctrl.signal.aborted) { item.status = 'queued'; item.progress = 0; return; }
          if (e.network) {
            // Connection gone: wait (2 s, 4 s … 30 s) and try again for as
            // long as the run is on; the attempt does not count.
            item.attempts -= 1;
            item.drops = (item.drops || 0) + 1;
            const wait = Math.min(MAX_BACKOFF_MS, 2000 * item.drops);
            item.status = 'retrying';
            item.error = `connection lost — retrying in ${Math.round(wait / 1000)} s (drop ${item.drops})`;
            setTimeout(() => { if (item.status === 'retrying') { item.status = 'queued'; this.pump(); } }, wait);
          } else if (item.attempts <= RETRIES) {
            item.status = 'retrying';
            item.error = `${e.message} — retry ${item.attempts}/${RETRIES}`;
            setTimeout(() => { if (item.status === 'retrying') { item.status = 'queued'; this.pump(); } }, 1500 * item.attempts);
          } else {
            this.finish(item, 'failed', e.message);
          }
        })
        .finally(() => {
          this.inFlight -= 1;
          this.controllers.delete(item.id);
          if (this.pending.length > 0) {
            this.emit('change');
            this.pump();
          } else {
            this.setRunning(false);
            this.emit('change');
            this.emit('idle');
          }
        });
    }
  }

  start() {
    if (!this.items.some((i) => i.status === 'queued')) return;
    this.setRunning(true);
    this.emit('change');
    this.pump();
  }
  stop() {
    this.setRunning(false);
    for (const c of this.controllers.values()) c.abort();
    for (const i of this.items) if (i.status === 'retrying') i.status = 'queued';
    this.emit('change');
  }
  retryFailed() {
    for (const i of this.items) if (i.status === 'failed') { i.status = 'queued'; i.attempts = 0; i.drops = 0; i.error = null; }
    this.start();
  }
  clearDone() {
    this.items = this.items.filter((i) => !['pushed', 'duplicate', 'skipped'].includes(i.status));
    this.emit('change');
  }
}

const queues = new Map();
export function getUploadQueue(eventId) {
  let q = queues.get(eventId);
  if (!q) { q = new UploadQueue(eventId); queues.set(eventId, q); }
  return q;
}

/** Re-render on queue activity; returns the queue. `onIdle` fires when a run ends. */
export function useUploadQueue(eventId, onIdle) {
  const queue = getUploadQueue(eventId);
  const [, bump] = useState(0);
  useEffect(() => queue.subscribe((kind) => {
    bump((v) => v + 1);
    if (kind === 'idle' && onIdle) onIdle();
  }), [queue, onIdle]);
  return queue;
}
